import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';

// Virtual grid moves remount cards. Remember successful exact URLs so those
// remounts do not hide an already-loaded image or replay its loading animation.
// Bound the number of status strings; no image bytes are held.
const loadedSources = new Set<string>();
const MAX_LOADED_SOURCES = 512;

interface ImageWithStatusProps {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  onBroken?: () => void;
}

const ImageWithStatus: React.FC<ImageWithStatusProps> = ({
  src,
  alt,
  style,
  onBroken,
}) => {
  const [loadedSource, setLoadedSource] = useState<string | null>(null);
  const [brokenSource, setBrokenSource] = useState<string | null>(null);
  const loading =
    loadedSource !== src && !loadedSources.has(src) && brokenSource !== src;
  const broken = brokenSource === src;
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {!broken && (
        <img
          key={src}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => {
            loadedSources.delete(src);
            loadedSources.add(src);
            if (loadedSources.size > MAX_LOADED_SOURCES)
              loadedSources.delete(loadedSources.values().next().value!);
            setLoadedSource(src);
            setBrokenSource(null);
          }}
          onError={() => {
            loadedSources.delete(src);
            setBrokenSource(src);
            onBroken?.();
          }}
          style={{
            ...style,
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.2s ease-in-out',
          }}
        />
      )}
      {loading && (
        <CircularProgress
          size={24}
          aria-label="Loading image"
          sx={{ position: 'absolute' }}
        />
      )}
      {broken && (
        <span
          role="img"
          aria-label="Image unavailable"
          style={{ color: '#777', fontSize: 12 }}
        >
          Image unavailable
        </span>
      )}
    </div>
  );
};

export default ImageWithStatus;
